<script setup lang="ts">
import { computed } from 'vue'
import { useCustomizerStore } from '@/stores/customizer'
import { useAuthStore } from '@/stores/authStore'
import type { UserRole } from '@/types/roles'
import sidebarItem, { type MenuItem } from './vertical-sidebar/sidebarItem'
import NavGroup from './vertical-sidebar/NavGroup/index.vue'
import NavItem from './vertical-sidebar/NavItem/index.vue'
import NavCollapse from './vertical-sidebar/NavCollapse/NavCollapse.vue'
import MylimniLogo from '@/components/MylimniLogo.vue'

// Single-panel MatDash sidebar: every group is visible with its header
// (the app has ~16 items — the demo's icon-rail + contextual-panel variant
// left the panel two-thirds empty and hid sections behind icons).
// customizer.mini_sidebar collapses it to an icon rail (expand on hover).
const customizer = useCustomizerStore()
const authStore = useAuthStore()

// Navigation dédiée par rôle : le sidebar affichait le même menu manager à tout
// le monde. Un super_admin y voyait des liens /manager/* que son guard rejette
// (nav cassée de bout en bout). On filtre chaque section/élément par `roles`.
// Rôle inconnu (chargement) : aucun filtrage, pour ne jamais afficher un menu
// vide au manager (rôle largement majoritaire) — la visibilité n'est pas une
// barrière de sécurité, les guards de route restent seuls décisionnaires.
const isVisible = (item: MenuItem, role?: UserRole): boolean =>
  !item.roles || !role || item.roles.includes(role)

const sidebarMenu = computed<MenuItem[]>(() => {
  const role = authStore.user?.role as UserRole | undefined
  return sidebarItem
    .filter((section) => isVisible(section, role))
    .map((section) => ({
      ...section,
      children: (section.children ?? []).filter((child) => isVisible(child, role))
    }))
    .filter((section) => !section.header || (section.children?.length ?? 0) > 0)
})
</script>

<template>
  <v-navigation-drawer
    v-model="customizer.Sidebar_drawer"
    elevation="0"
    rail-width="80"
    app
    top="0"
    class="leftSidebar"
    :rail="customizer.mini_sidebar"
    expand-on-hover
    width="256"
    :permanent="!$vuetify.display.mobile"
    :temporary="$vuetify.display.mobile"
  >
    <div class="pa-4 pb-0 d-flex align-center">
      <MylimniLogo />
    </div>

    <perfect-scrollbar class="scrollnavbar">
      <div class="px-4 py-0 sidebar-menus">
        <v-list class="py-1">
          <template
            v-for="(item, i) in sidebarMenu"
            :key="i"
          >
            <NavGroup
              v-if="item.header"
              :key="item.title"
              :item="item"
            />
            <template
              v-for="sItem in item.children"
              :key="sItem.title"
            >
              <NavCollapse
                v-if="sItem.children"
                class="leftPadding"
                :item="sItem"
                :level="0"
              />
              <NavItem
                v-else
                :item="sItem"
                class="leftPadding"
              />
            </template>
          </template>
        </v-list>
      </div>
    </perfect-scrollbar>
  </v-navigation-drawer>
</template>
